import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEmailConnectors } from '@/hooks/useEmailConnectors';
import { useWorkflowTemplates } from '@/hooks/useWorkflowTemplates';

import { EmailPreview } from '../EmailPreview';
import { TemplateCodeEditor } from '../TemplateCodeEditor';
import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { EmailConnector, SendEmailNodeData } from '@repo/shared-types';

interface SendEmailConfigProps {
  data: SendEmailNodeData;
  onChange: (data: SendEmailNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const SendEmailConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: SendEmailConfigProps) => {
  const { data: templates } = useWorkflowTemplates();
  const { data: connectors = [] } = useEmailConnectors();
  const [editorOpen, setEditorOpen] = useState(false);

  const update = (patch: Partial<SendEmailNodeData>) => onChange({ ...data, ...patch });
  const selectedConnector = connectors.find((c: EmailConnector) => c.id === data.connectorId);

  const emailTemplates = templates?.filter((t) => t.type === 'email') ?? [];

  const handleTemplateSelect = (id: string) => {
    const tpl = emailTemplates.find((t) => t.id === id);
    if (tpl) {
      update({
        templateId: id,
        subject: tpl.subject ?? data.subject,
        body: tpl.body,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <VariableAutocomplete
          value={data.label}
          onChange={(v) => update({ label: v })}
          placeholder="Node label"
          responseFieldsSchema={responseFieldsSchema}
          reviewNodeMetas={reviewNodeMetas}
          classifierNodeMetas={classifierNodeMetas}
          qualityNodeMetas={qualityNodeMetas}
          agentNodeMetas={agentNodeMetas}
          analyticsNodeMetas={analyticsNodeMetas}
        />
      </div>

      {connectors.length > 0 && (
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">Connector</label>
          <Select
            value={data.connectorId ?? '__default__'}
            onValueChange={(v) => {
              update({ connectorId: v === '__default__' ? null : v, fromEmail: null });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Default (legacy settings)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__default__">Default (legacy settings)</SelectItem>
              {connectors
                .filter((c: EmailConnector) => c.is_active)
                .map((c: EmailConnector) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} ({c.provider_type})
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {selectedConnector && selectedConnector.email_addresses.length > 0 && (
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">From Email</label>
          <Select
            value={data.fromEmail ?? ''}
            onValueChange={(v) => update({ fromEmail: v || null })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select from email..." />
            </SelectTrigger>
            <SelectContent>
              {selectedConnector.email_addresses.map((addr: string) => (
                <SelectItem key={addr} value={addr}>
                  {addr}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {emailTemplates.length > 0 && (
        <div className="space-y-1">
          <label className="text-muted-foreground text-xs font-medium">Template</label>
          <Select value={data.templateId ?? ''} onValueChange={handleTemplateSelect}>
            <SelectTrigger>
              <SelectValue placeholder="Load from template..." />
            </SelectTrigger>
            <SelectContent>
              {emailTemplates.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">To</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.to}
            onChange={(v) => update({ to: v })}
            placeholder="{{contact.email}}"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            className="flex-1"
          />
          <VariablePicker
            onSelect={(v) => update({ to: data.to + v })}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Subject</label>
        <div className="flex gap-1">
          <VariableAutocomplete
            value={data.subject}
            onChange={(v) => update({ subject: v })}
            placeholder="Email subject"
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            className="flex-1"
          />
          <VariablePicker
            onSelect={(v) => update({ subject: data.subject + v })}
            responseFieldsSchema={responseFieldsSchema}
            reviewNodeMetas={reviewNodeMetas}
            classifierNodeMetas={classifierNodeMetas}
            qualityNodeMetas={qualityNodeMetas}
            agentNodeMetas={agentNodeMetas}
            analyticsNodeMetas={analyticsNodeMetas}
            triggerType={triggerType}
          />
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="text-muted-foreground text-xs font-medium">Body</label>
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setEditorOpen(true)}
          >
            Edit Template
          </Button>
        </div>
        {data.body ? (
          <div
            className="border-input bg-muted/30 max-h-32 cursor-pointer overflow-hidden rounded-md border p-2 text-xs"
            onClick={() => setEditorOpen(true)}
          >
            <p className="text-muted-foreground line-clamp-5 font-mono">{data.body}</p>
          </div>
        ) : (
          <div
            className="border-input text-muted-foreground cursor-pointer rounded-md border border-dashed p-4 text-center text-xs"
            onClick={() => setEditorOpen(true)}
          >
            Click to edit template body...
          </div>
        )}
      </div>

      <TemplateCodeEditor
        value={data.body ?? ''}
        onChange={(v) => update({ body: v })}
        type="email"
        subject={data.subject}
        onSubjectChange={(v) => update({ subject: v })}
        reviewSchema={responseFieldsSchema}
        reviewNodeMetas={reviewNodeMetas}
        classifierNodeMetas={classifierNodeMetas}
        qualityNodeMetas={qualityNodeMetas}
        agentNodeMetas={agentNodeMetas}
        analyticsNodeMetas={analyticsNodeMetas}
        open={editorOpen}
        onOpenChange={setEditorOpen}
      />

      {data.body && <EmailPreview html={data.body} subject={data.subject} variables={{}} />}
    </div>
  );
};
